import * as React from 'react';
import { connect, useSelector } from 'react-redux';
import cn from 'classnames';

import Passenger from '../../../../../PassengerForm/components/PassengerForm';
import { PassengerState } from '../../../../store/passengers/reducers';
import { AdditionalTravellerFieldEnum, InputType } from '../../../../../Passenger/types';
import { useTheme } from '../../../../../theme';
import memes from '../../../../../memes';
import { CheckoutState } from '../../../../store/reducers';
import { getPassengerLabels } from '../../../../store/passengers/selectors';
import { getIsNew, getIsReadonly, getServices, getMessages } from '../../../../store/order/selectors';
import { Passenger as PassengerType, PassengerLabels, FormValues } from '../../../../types';
import { getPassengerStateCompatible } from '../../../../store/selectedServices/selectors';
import { useRenderers } from '../../../../../renderProps';
import PassengerSubsidy from './PassengerSubsidy/PassengerSubsidy';
import { OrderSubsidyState } from '../SubsidyRequest/subsidyHook';
import {
	AviaPassengerType,
	GdsServicesFragment,
	GetAccountInfoQuery,
	TravellerFieldEnum,
	TravellerProfileFragment
} from '@websky/graphql';
import LoyaltyConnect, {
	ConnectType
} from '../../../../../Loyalty/components/LoyaltyStub/LoyaltyConnect/LoyaltyConnect';
import { getUserInfo } from '../../../../utils';
import DisabilitiesContextProvider from '../../../../../PassengerForm/components/components/Disabilities/context';
import { useConfigRedirects } from '../../../../../hooks';
import { usePassengers } from './hooks';
import { getSubsidiesByPassengers } from '../../../../store/subsidies/selectors';

interface OwnProps {
	className?: string;
	passengers: PassengerType[];
	isAuthorized?: boolean;
	onFill: (values: TravellerProfileFragment, id: number, fieldValue?: string) => void;
	isInputsReadonly?: boolean;
	inModal?: boolean;
	isChangePassengers?: boolean;
	orderSubsidies?: OrderSubsidyState;
	selectedProfileIdsByPassenger?: Map<number, string>;
	backToResults?: () => void;
	autofocus?: boolean;
	showLoginForm?: () => void;
	userInfo?: GetAccountInfoQuery['CurrentUser'];
	refetchUser?: () => void;
	isLoginFormOpen?: boolean;
	isChangeFiles?: boolean;
	onSwap?: (id: number) => void;
	onToggleChangeFiles?: (expanded: boolean) => void;
	onChangeFormSubscription?: (values: FormValues) => void;
	hideClearButton?: boolean;
}

interface StateProps {
	isReadonly: boolean;
	isNew: boolean;
	labels: PassengerLabels;
	passengersState: PassengerState[];
	services: GdsServicesFragment['services'];
}

export const getInputType = memes(
	(type: TravellerFieldEnum | AdditionalTravellerFieldEnum): InputType => {
		switch (type) {
			case TravellerFieldEnum.Gender:
				return InputType.Switch;

			case TravellerFieldEnum.Phone:
				return InputType.Phone;

			case TravellerFieldEnum.BirthDate:
			case TravellerFieldEnum.DocExpiryDate:
				return InputType.Date;

			case TravellerFieldEnum.LoyaltyAirline:
			case TravellerFieldEnum.DocType:
				return InputType.Select;

			case TravellerFieldEnum.FirstName:
			case TravellerFieldEnum.LastName:
				return InputType.Autocomplete;

			case TravellerFieldEnum.Nationality:
				return InputType.Nationality;

			default:
				return InputType.Text;
		}
	}
);

const Passengers: React.FunctionComponent<OwnProps & StateProps> = ({
	className,
	passengersState,
	isReadonly,
	isNew,
	isAuthorized,
	passengers,
	services,
	onFill,
	hideClearButton,
	labels,
	isInputsReadonly,
	inModal,
	isChangePassengers,
	orderSubsidies,
	backToResults,
	autofocus,
	showLoginForm,
	userInfo,
	refetchUser,
	isLoginFormOpen,
	isChangeFiles,
	selectedProfileIdsByPassenger,
	onSwap,
	onToggleChangeFiles,
	onChangeFormSubscription
}) => {
	const theme = useTheme('Checkout').PassengersStyles;
	const { CheckoutFormPassenger = Passenger } = useRenderers();

	const [openedPassengerFormId, setOpenedPassengerFormId] = React.useState(0);
	const [mobileOpenedPassengerId, setMobileOpenedPassengerId] = React.useState(null);
	const [loginBeforeCreateLoyaltyAccount, setLoginBeforeCreateLoyaltyAccount] = React.useState(false);
	const [loyaltyConnectOpen, setLoyaltyConnectOpen] = React.useState(false);
	const subsidiesByPassengers = useSelector(getSubsidiesByPassengers);

	const orderMessages = useSelector(getMessages);

	const {
		travellersMap,
		passengerLabelMap,
		passengerFieldsMap,
		servicesPricesMap,
		accompanyingFields
	} = usePassengers({
		passengers,
		labels,
		services,
		passengersState,
		isChangePassengers,
		onChangeFormSubscription,
		availableSubsidies: subsidiesByPassengers,
		isAuthorized
	});

	const { onGoToAccountSettings } = useConfigRedirects();

	const onCorrectFormFilledHandler = (passengerId: number) => {
		if (passengerId + 1 > openedPassengerFormId) {
			setOpenedPassengerFormId(passengerId + 1);
		}
	};

	const onSignUpLoyaltyProgram = () => {
		if (!isAuthorized) {
			setLoginBeforeCreateLoyaltyAccount(true);
			showLoginForm();
		} else {
			setLoyaltyConnectOpen(true);
		}
	};

	React.useEffect(() => {
		if (isAuthorized && loginBeforeCreateLoyaltyAccount && userInfo.isFfpAuthorizationNeeded) {
			setLoyaltyConnectOpen(true);
		}
	}, [loginBeforeCreateLoyaltyAccount, isAuthorized]);

	React.useEffect(() => {
		if (!isLoginFormOpen) {
			setLoginBeforeCreateLoyaltyAccount(false);
		}
	}, [isLoginFormOpen]);

	const onLoyaltyCardSaved = () => {
		setLoyaltyConnectOpen(false);
		refetchUser();
	};

	return (
		<div className={cn(theme.passengers, { [theme.passengers_readonly]: isReadonly })}>
			{passengers.map((passenger, passengerId) => {
				const label = passengerLabelMap.get(passengerId);
				const passengerFields = passengerFieldsMap.get(passengerId);
				const servicesPrice = servicesPricesMap.get(passengerId);
				const traveller = travellersMap.get(passenger.id);

				const profileId = selectedProfileIdsByPassenger.get(passengerId);
				const passAccompanyingFields = passengerId + 1 === travellersMap.size ? accompanyingFields : [];

				return (
					<DisabilitiesContextProvider
						value={{
							availableDisabilities: passenger.availableDisabilities,
							passengerId
						}}
						key={passenger.id}
					>
						<PassengerSubsidy
							passengerId={passenger.id}
							subsidyInfo={traveller.subsidyInfo?.subsidies}
							fields={traveller.values}
							orderSubsidyState={orderSubsidies}
							backToResults={backToResults}
						>
							{props => (
								<CheckoutFormPassenger
									hideClearButton={hideClearButton}
									key={passengerId}
									className={className}
									id={passengerId}
									onFill={(values: TravellerProfileFragment, fieldValue?: string) => {
										onFill(values, passengerId, fieldValue);
									}}
									fields={passengerFields}
									passengerType={passenger.type as AviaPassengerType}
									supplierType={passenger.supplierTravellerType}
									selectedProfileId={profileId}
									isReadonly={isReadonly}
									isAuthorized={isAuthorized}
									isChangePassengers={isChangePassengers}
									servicesTotalPrice={servicesPrice}
									withSubsidies={props.withSubsidy}
									noSubsidyAvailable={props.noSubsidyAvailable}
									subsidyRejectMessage={props.subsidyRejectMessage}
									label={label}
									isInputsReadonly={isInputsReadonly}
									onCorrectFilled={onCorrectFormFilledHandler}
									open={!isNew || inModal || passengerId <= openedPassengerFormId}
									mobileOpenedPassengerId={mobileOpenedPassengerId}
									onSetMobileOpenedPassengerId={setMobileOpenedPassengerId}
									needOthsSsr={passenger.needOthsSsr}
									autofocus={autofocus && passengerId === 0}
									isChangeFiles={isChangeFiles}
									accompanyingFields={passAccompanyingFields}
									onSignUpLoyaltyProgram={onSignUpLoyaltyProgram}
									onSwap={onSwap ? () => onSwap(passengerId) : null}
									onToggleChangeFiles={onToggleChangeFiles}
									infoMessages={orderMessages}
								/>
							)}
						</PassengerSubsidy>

						<LoyaltyConnect
							isOpen={loyaltyConnectOpen}
							type={ConnectType.Login}
							userInfo={getUserInfo(userInfo)}
							showRegisterAction={true}
							onLoyaltyCardSaved={onLoyaltyCardSaved}
							onClose={() => setLoyaltyConnectOpen(false)}
							onGoToSettings={onGoToAccountSettings}
						/>
					</DisabilitiesContextProvider>
				);
			})}
		</div>
	);
};

const mapStateToProps = (state: CheckoutState, ownProps: OwnProps): StateProps & OwnProps => {
	return {
		...ownProps,
		labels: getPassengerLabels(state),
		passengersState: getPassengerStateCompatible(state),
		services: getServices(state),
		isNew: getIsNew(state),
		isReadonly: getIsReadonly(state)
	};
};

export default connect(mapStateToProps)(Passengers);
